import React, { useEffect, useMemo, useCallback } from 'react';
import classNames from 'classnames/bind';
import { useSnapCarousel } from 'react-snap-carousel';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  useTheme,
} from '@mui/material';
import { getTop3Swells, trackEvent } from '@surfline/web-common';

import ConsistencyCell from 'components/TableCell/ConsistencyCell';
import EnergyCell from 'components/TableCell/EnergyCell';
import PressureCell from 'components/TableCell/PressureCell';
import ProbabilityCell from 'components/TableCell/ProbabilityCell';
import RatingsCell from 'components/TableCell/RatingsCell';
import SurfCell from 'components/TableCell/SurfCell';
import SwellCell from 'components/TableCell/SwellCell';
import TimeCell from 'components/TableCell/TimeCell';
import WeatherCell from 'components/TableCell/WeatherCell';
import WindCell from 'components/TableCell/WindCell';
import { useMaxWidthTablet, useMaxWidthDesktop } from 'hooks/useMediaQueries';
import type { ConditionRating } from 'types/conditionRating';
import type { ConsistencyTimestep } from 'types/consistency';
import type { SurfObservation, SurfTimestep } from 'types/surf';
import type { SwellsTimestep } from 'types/swell';
import type { Height, Speed, Units } from 'types/units';
import type { WeatherDetail } from 'types/weather';
import type { WindTimestep } from 'types/wind';
import type { WaveEnergyGraphData } from 'types/waveEnergy';
import { getNearshoreKjForSwellHour } from 'utils/waveEnergyTableDisplay';
import TableHeader from '../TableHeader';
import { useMaxWidthLandscapeMobile } from '../hooks/useTableData';

import styles from './ForecastTableRows.module.scss';

const cx = classNames.bind(styles);

export type ForecastTableRowsCell =
  | 'consistency'
  | 'energy'
  | 'pressure'
  | 'probability'
  | 'rating'
  | 'surf'
  | 'swellOne'
  | 'swellThree'
  | 'swellTwo'
  | 'time'
  | 'weather'
  | 'wind';

interface ForecastTableRowProps {
  dayIndex: number;
  columnsToShow: Array<ForecastTableRowsCell>;
  isMobile: boolean;
  isSurfObserved?: boolean;
  isWindObserved?: boolean;
  loading: boolean;
  observedData: {
    surf?: {
      max: number;
      min: number;
      raw?: {
        max: number;
        min: number;
      };
      plus?: boolean;
    };
  };
  rating: string | undefined;
  consistency: ConsistencyTimestep;
  showObservedData?: boolean;
  slideIndex: number;
  surf?: SurfTimestep;
  swellUnits: Height | string | undefined;
  swells: SwellsTimestep;
  useNearshoreFromWaveEnergy?: boolean;
  waveEnergy?: WaveEnergyGraphData;
  waveUnits: Height | string | undefined;
  weatherHour: WeatherDetail;
  weatherIconPath: string;
  weatherUnits: Units | null;
  windHour: WindTimestep;
  windUnits: Speed | string | undefined;
}

const ForecastTableRow: React.FC<ForecastTableRowProps> = ({
  dayIndex,
  columnsToShow,
  isMobile,
  isSurfObserved = false,
  isWindObserved = false,
  loading,
  observedData,
  rating,
  consistency,
  showObservedData,
  slideIndex,
  surf,
  swellUnits,
  swells,
  useNearshoreFromWaveEnergy = false,
  waveEnergy,
  waveUnits,
  weatherHour,
  weatherIconPath,
  weatherUnits,
  windHour,
  windUnits,
}) => {
  const sortedTop3Swells = useMemo(() => getTop3Swells(swells?.swells || []), [swells?.swells]);

  const energyKj = useMemo(() => {
    if (!useNearshoreFromWaveEnergy || !waveEnergy) {
      return swells?.power;
    }
    return getNearshoreKjForSwellHour(waveEnergy, dayIndex, swells) ?? swells?.power;
  }, [dayIndex, swells, useNearshoreFromWaveEnergy, waveEnergy]);

  const noSwell = useMemo(
    () =>
      swells?.swells?.every(
        (swell) => swell.direction === 0 && swell.height === 0 && swell.period === 0,
      ),
    [swells?.swells],
  );

  const cellFlex = useCallback((column: ForecastTableRowsCell) => {
    if (column?.includes('swell')) return 5;
    if (column === 'surf' || column === 'rating') return 2;
    if (column === 'wind') return 4;

    return 3;
  }, []);

  const filteredColumnsToShow = (columnsToShow as Array<ForecastTableRowsCell>).filter(
    (column) => column !== 'rating',
  );

  return (
    <TableRow
      className={cx([styles.row, styles.scrollableRow])}
      data-testid="forecast-table-scrollable-row"
    >
      {filteredColumnsToShow.map((column: ForecastTableRowsCell, columnIndex: number) => (
        <TableCell
          className={cx(styles.cell, {
            [styles.surfCell]: column === 'surf',
          })}
          style={{ flex: cellFlex(column) }}
          data-testid="forecast-table-rows-cell"
          key={`${column}-${columnIndex + 1}`}
        >
          <>
            {columnIndex > 1 || slideIndex > 0 ? (
              <div className={styles.divider} data-testid="forecast-table-row-cell-divider" />
            ) : null}
            {column === 'swellOne' && (
              <SwellCell
                loading={loading}
                direction={sortedTop3Swells?.[0]?.direction}
                noSwell={noSwell}
                rating={rating}
                slideIndex={slideIndex}
                swellHeight={sortedTop3Swells?.[0]?.height}
                swellHeightUnits={swellUnits}
                swellInterval={sortedTop3Swells?.[0]?.period}
                swellIntervalUnits="S"
                isPrimary
              />
            )}
            {column === 'swellTwo' && (
              <SwellCell
                loading={loading}
                direction={sortedTop3Swells?.[1]?.direction}
                noSwell={noSwell}
                swellHeight={sortedTop3Swells?.[1]?.height}
                swellHeightUnits={swellUnits}
                swellInterval={sortedTop3Swells?.[1]?.period}
                swellIntervalUnits="S"
              />
            )}
            {column === 'swellThree' && (
              <SwellCell
                loading={loading}
                direction={sortedTop3Swells?.[2]?.direction}
                noSwell={noSwell}
                swellHeight={sortedTop3Swells?.[2]?.height}
                swellHeightUnits={swellUnits}
                swellInterval={sortedTop3Swells?.[2]?.period}
                swellIntervalUnits="S"
              />
            )}
            {column === 'wind' && (
              <WindCell
                isDataObserved={isWindObserved}
                loading={loading}
                direction={windHour?.direction ?? null}
                directionType={windHour?.directionType}
                speed={windHour?.speed}
                gust={windHour?.gust}
                units={windUnits}
              />
            )}
            {column === 'surf' && surf && (
              <SurfCell
                dark={false}
                loading={loading}
                isDataObserved={isSurfObserved && showObservedData}
                isMobile={isMobile}
                max={
                  observedData?.surf?.max && showObservedData
                    ? observedData.surf.max
                    : surf?.surf?.max
                }
                min={
                  observedData?.surf?.min && showObservedData
                    ? observedData.surf.min
                    : surf?.surf?.min
                }
                rawMax={
                  observedData?.surf?.raw?.max && showObservedData
                    ? observedData?.surf?.raw?.max
                    : surf?.surf?.raw?.max
                }
                plus={
                  observedData?.surf?.plus && showObservedData
                    ? observedData?.surf?.plus
                    : surf?.surf?.plus
                }
                units={waveUnits}
              />
            )}
            {column === 'rating' && <RatingsCell loading={loading} rating={rating} />}
            {column === 'energy' && <EnergyCell loading={loading} energy={energyKj} />}
            {column === 'consistency' && (
              <ConsistencyCell
                loading={loading}
                consistency={consistency?.consistency?.waveCount}
              />
            )}
            {column === 'pressure' && (
              <PressureCell
                loading={loading}
                pressure={weatherHour?.pressure}
                units={weatherUnits?.pressure || 'MB'}
              />
            )}
            {column === 'weather' && (
              <WeatherCell
                loading={loading}
                icon={weatherIconPath}
                temperature={weatherHour?.temperature}
                condition={weatherHour?.condition}
                units={weatherUnits?.temperature}
              />
            )}
            {column === 'probability' && (
              <ProbabilityCell loading={loading} probability={swells?.probability} />
            )}
          </>
        </TableCell>
      ))}
    </TableRow>
  );
};

const ForecastTableFixedColumns = ({
  swellsHours,
  loading,
  isMobile,
  ratingsHours,
}: {
  swellsHours: Array<SwellsTimestep>;
  loading: boolean;
  isMobile: boolean;
  ratingsHours: Array<ConditionRating>;
}) => (
  <Table className={cx(styles.table, styles.fixedTable)} data-testid="forecast-table">
    <TableBody>
      {swellsHours.map((hour: SwellsTimestep, hourIndex: number) => (
        <TableRow
          className={styles.row}
          data-testid="forecast-table-fixed-row"
          key={swellsHours?.[hourIndex]?.timestamp}
        >
          <TableCell
            className={cx(styles.cell, styles.timeCellContainer)}
            data-testid="forecast-table-rows-cell"
          >
            <TimeCell
              loading={loading}
              timestamp={hour?.timestamp}
              isMobile={isMobile}
              utcOffset={hour?.utcOffset}
            />
          </TableCell>
          <TableCell className={styles.cell} data-testid="forecast-table-rows-cell">
            <RatingsCell loading={loading} rating={ratingsHours?.[hourIndex]?.rating} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

interface ForecastTableRowsProps {
  dayIndex: number;
  hideProbability: boolean;
  isExpanded: boolean;
  isHeaderSticky: boolean;
  isHistoricView: boolean;
  loading: boolean;
  observations?: Array<SurfObservation> | undefined;
  ratingsHours: Array<ConditionRating>;
  consistency: Array<ConsistencyTimestep>;
  showObservedData?: boolean;
  surfHours: Array<SurfTimestep>;
  surfUnits: Units | null;
  swellsHours: Array<SwellsTimestep>;
  toggleDayExpanded: () => void;
  waveUnits: Units | null;
  weatherHours: Array<WeatherDetail>;
  weatherIconPath: string;
  weatherUnits: Units | null;
  windHours: Array<WindTimestep>;
  windUnits: Units | null;
  useNearshoreFromWaveEnergy?: boolean;
  waveEnergy?: WaveEnergyGraphData;
}

const ForecastTableRows: React.FC<ForecastTableRowsProps> = ({
  dayIndex,
  hideProbability,
  isExpanded,
  isHeaderSticky,
  isHistoricView,
  loading,
  observations,
  ratingsHours,
  consistency,
  showObservedData,
  surfHours,
  surfUnits,
  swellsHours,
  toggleDayExpanded,
  waveUnits,
  weatherHours,
  weatherIconPath,
  weatherUnits,
  windHours,
  windUnits,
  useNearshoreFromWaveEnergy = false,
  waveEnergy,
}) => {
  const theme = useTheme();
  const isMobile = useMaxWidthTablet();
  const isTablet = useMaxWidthDesktop();
  const isLandscapeMobile = useMaxWidthLandscapeMobile();

  const { scrollRef, pages, activePageIndex, goTo, snapPointIndexes, refresh } = useSnapCarousel();

  const slides = useMemo(() => {
    const probability = hideProbability ? null : 'probability';
    const weather = isHistoricView ? null : 'weather';
    const rating = isLandscapeMobile || !ratingsHours?.[0]?.rating ? null : 'rating';
    const consistencySlide = !isHistoricView ? 'consistency' : null;
    const pressure = isHistoricView ? null : 'pressure';
    const lastSlide = isHistoricView
      ? null
      : ['energy', consistencySlide, weather, pressure, probability].filter((item) =>
          Boolean(item),
        );

    const singleSlide = [
      [
        'surf',
        rating,
        'swellOne',
        'swellTwo',
        'swellThree',
        'wind',
        'energy',
        consistencySlide,
        weather,
        pressure,
        probability,
      ].filter((item) => Boolean(item)),
    ];

    if (isMobile) {
      return [
        ['surf', 'swellOne', 'wind'],
        ['swellOne', 'swellTwo', 'swellThree'],
        lastSlide,
      ].filter((item) => Boolean(item));
    }

    if (isTablet) {
      return [
        ['surf', rating, 'swellOne', 'swellTwo', 'swellThree', 'wind'].filter((item) =>
          Boolean(item),
        ),
        lastSlide,
      ].filter((item) => Boolean(item));
    }

    return singleSlide;
  }, [
    hideProbability,
    isHistoricView,
    isLandscapeMobile,
    isMobile,
    isTablet,
    ratingsHours,
  ]) as Array<Array<ForecastTableRowsCell>>;

  const tables = useMemo(
    () =>
      slides.map((slide, slideIndex) => (
        <Table
          className={cx([styles.table, styles.scrollableTable])}
          data-testid="forecast-table"
          key={`${slide?.[0]}${slideIndex + 1}`}
          style={{
            scrollSnapAlign: snapPointIndexes?.has(slideIndex) ? 'start' : 'none',
          }}
        >
          <TableBody className={styles.tableBody}>
            {swellsHours.map((hourSwells: SwellsTimestep, hourIndex: number) => {
              const hourSurf = surfHours.find(
                (surf) =>
                  surf.timestamp === hourSwells.timestamp &&
                  surf.utcOffset === hourSwells.utcOffset,
              );
              const hourObservation = observations?.find(
                (observation) =>
                  observation.timestamp === hourSwells.timestamp &&
                  observation.utcOffset === hourSwells.utcOffset,
              );
              const isSurfObserved = !!(
                hourObservation?.surf?.type &&
                (hourObservation?.surf?.type === 'HUMAN' ||
                  hourObservation?.surf?.type === 'CAMERA')
              );

              let observedData = {};

              if (isSurfObserved) {
                observedData = {
                  ...observedData,
                  surf: {
                    min: hourObservation.surf?.min,
                    max: hourObservation.surf?.max,
                    raw: { min: hourObservation.surf?.raw.min, max: hourObservation.surf?.raw.max },
                    plus: hourObservation.surf?.plus,
                  },
                };
              }

              return (
                <ForecastTableRow
                  dayIndex={dayIndex}
                  columnsToShow={slide as Array<ForecastTableRowsCell>}
                  isMobile={isMobile}
                  isSurfObserved={isSurfObserved && showObservedData}
                  key={`${hourIndex + 1}`}
                  loading={loading}
                  observedData={observedData}
                  rating={ratingsHours?.[hourIndex]?.rating}
                  consistency={consistency?.[hourIndex]}
                  showObservedData={showObservedData}
                  slideIndex={slideIndex}
                  swellUnits={waveUnits?.swellHeight}
                  swells={hourSwells}
                  surf={hourSurf}
                  useNearshoreFromWaveEnergy={useNearshoreFromWaveEnergy}
                  waveEnergy={waveEnergy}
                  waveUnits={waveUnits?.waveHeight}
                  weatherHour={weatherHours?.[hourIndex]}
                  weatherIconPath={weatherIconPath}
                  weatherUnits={weatherUnits}
                  windHour={windHours?.[hourIndex]}
                  windUnits={windUnits?.windSpeed}
                />
              );
            })}
          </TableBody>
        </Table>
      )),
    [
      slides,
      snapPointIndexes,
      swellsHours,
      surfHours,
      observations,
      isMobile,
      showObservedData,
      loading,
      ratingsHours,
      consistency,
      dayIndex,
      useNearshoreFromWaveEnergy,
      waveEnergy,
      waveUnits?.swellHeight,
      waveUnits?.waveHeight,
      weatherHours,
      weatherIconPath,
      weatherUnits,
      windHours,
      windUnits?.windSpeed,
    ],
  );

  // disable exhaustive-deps rule to avoid isMobile and isTablet from firing trackevent
  useEffect(() => {
    if (isMobile || isTablet) {
      trackEvent('Table View Updated', {
        positionNumber: activePageIndex,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePageIndex]);

  useEffect(() => {
    refresh();
  }, [isMobile, isTablet, refresh]);

  return (
    <>
      {(dayIndex === 0 || isExpanded) && (
        <TableHeader
          surfUnits={surfUnits?.waveHeight}
          hideProbability={hideProbability}
          pageIndex={activePageIndex}
          ratingsHours={ratingsHours}
          isHeaderSticky={false}
          isHistoricView={isHistoricView}
        />
      )}
      {
        /* When the header is sticky we need to duplicate it to avoid
        a content shift as the sticky header changes to fixed positioning */
        isHeaderSticky && !isMobile && (
          <TableHeader
            surfUnits={waveUnits?.swellHeight}
            hideProbability={hideProbability}
            pageIndex={activePageIndex}
            ratingsHours={ratingsHours}
            isHeaderSticky
            isHistoricView={isHistoricView}
          />
        )
      }
      <TableContainer className={styles.container} key={JSON.stringify(pages)}>
        <Box
          onClick={toggleDayExpanded}
          className={styles.containerInner}
          data-testid="forecast-table-rows-container"
        >
          <ForecastTableFixedColumns
            ratingsHours={ratingsHours}
            swellsHours={swellsHours}
            loading={loading}
            isMobile={isMobile}
          />
          <div className={styles.scrollContainer} ref={scrollRef}>
            {tables}
          </div>
        </Box>
        {slides?.length > 1 ? (
          <div
            className={styles.controlsWrapper}
            data-testid="forecast-table-rows-controls-wrapper"
          >
            {pages.map((_, pageIndex) => (
              <button
                aria-label="mobile table controls"
                className={styles.carouselControls}
                data-testid="forecast-table-rows-control-button"
                key={`button${pageIndex + 1}`}
                onClick={() => goTo(pageIndex)}
                type="button"
                style={{
                  background:
                    pageIndex === activePageIndex
                      ? theme.palette.text.brandSecondary
                      : theme.palette.text.secondary,
                }}
              />
            ))}
          </div>
        ) : null}
      </TableContainer>
    </>
  );
};

export default ForecastTableRows;
